import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import type { ProjectConnectionDetailsDto, ProjectConnectionGroupDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Pagination } from "@/components/Pagination/Pagination";
import { Button } from "@/design-system/Button";
import { SectionCard } from "@/design-system/SectionCard";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface ProjectConnectionGroupsListProps {
  projectConnection: ProjectConnectionDetailsDto;
  projectConnectionGroups: ProjectConnectionGroupDto[];
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  totalPages: number;
  onDelete: (groupId: string) => Promise<unknown>;
}

export function ProjectConnectionGroupsList({
  projectConnection,
  projectConnectionGroups,
  isLoading,
  page,
  onPageChange,
  totalPages,
  onDelete,
}: ProjectConnectionGroupsListProps): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: connectionId } = useParams<{ id: string }>();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("pc-group-delete-modal");

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<ProjectConnectionGroupDto>();

    return [
      helper.accessor("name", {
        header: t("page.project-connection-groups.list.table.name"),
        cell: (cell) => cell.getValue(),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("type", {
        header: t("page.project-connection-groups.list.table.type"),
        cell: (cell) => {
          switch (cell.getValue()) {
            case "interest":
              return (
                <span className="whitespace-nowrap">
                  {t("page.project-connection-groups.form.type.option.interest-group")}
                </span>
              );
            case "helpCategory":
              return (
                <span className="whitespace-nowrap">
                  {t("page.project-connection-groups.form.type.option.help-category")}
                </span>
              );
          }
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const id = cell.getValue();

          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => void navigate(routes.projectConnections.editGroup({ id: connectionId!, gid: id })),
            },
            {
              labels: {
                default: t("common.action.delete"),
              },
              value: "delete",
              icon: <Trash02Icon />,
              callbackFn: () => openDeleteModal(id),
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, navigate, connectionId, openDeleteModal]);

  const tableInstance = useResizableTable(TABLE_KEYS.PROJECT_CONNECTION_GROUPS, {
    columns,
    data: projectConnectionGroups,
    enableSorting: false,
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.project-connection-groups.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.project-connections.title"),
              to: routes.projectConnections.list(),
            },
            {
              name: projectConnection.name,
            },
            {
              name: t("page.project-connection-groups.list.title"),
            },
          ]}
        />
      }
      actions={
        <Button type="link" href={routes.projectConnections.createGroup({ id: connectionId! })}>
          {t("page.project-connection-groups.list.new-group")}
        </Button>
      }
    >
      {(projectConnectionGroups.length > 0 || isLoading) && (
        <Table table={tableInstance} isLoading={isLoading}>
          {totalPages > 1 && <Pagination count={totalPages} onChange={onPageChange} currentIndex={page} />}
        </Table>
      )}
      {!isLoading && !projectConnectionGroups.length && (
        <SectionCard>
          <p>{t("page.project-connection-groups.list.table.no-data")}</p>
        </SectionCard>
      )}
      <DeleteModal
        title={t("page.project-connection-groups.modal.delete.title")}
        description={t("page.project-connection-groups.modal.delete.description")}
        onDelete={(groupId) => onDelete(groupId)}
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
    </DocumentPage>
  );
}
